import { apiClient } from '@shared/utils/api-client';

export type SellerApplicationStatus =
  | 'DRAFT'
  | 'PENDING_MODERATION'
  | 'APPROVED'
  | 'REJECTED';

/** Совпадает с Prisma enum `SellerApplicationRejectionKind`. */
export type SellerApplicationRejectionKind =
  | 'POOR_DOCUMENT_PHOTOS'
  | 'DATA_MISMATCH'
  | 'INCOMPLETE_DOCUMENTS'
  | 'REQUIRES_REVISION';

export interface SellerApplicationRow {
  id: number;
  publicToken: string;
  status: SellerApplicationStatus;
  currentStep: number;
  accountType: string | null;
  inn: string | null;
  companyName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  submittedAt: string | null;
  moderatedAt: string | null;
  moderationRejectionReason: string | null;
  moderationRejectionKind: SellerApplicationRejectionKind | null;
  moderationRejectionComment: string | null;
  sellerId: number | null;
  offerAccepted?: boolean;
  seller: { id: number; slug: string; shopName: string } | null;
  moderatedBy: { id: number; email: string; name: string | null } | null;
  user: { id: number; phone: string | null; email?: string | null } | null;
  pendingShopName?: string | null;
  pendingActivityDescription?: string | null;
  pendingLogoFileUrl?: string | null;
  profileChangesSubmittedAt?: string | null;
  profileChangesRejectionReason?: string | null;
  profileChangesRejectedAt?: string | null;
}

/** Поля витрины, которые продавец меняет через модерацию. */
export type SellerProfileChangeField = 'shopName' | 'activityDescription' | 'logo';

export interface SellerProfileChangesRow {
  id: number;
  sellerId: number | null;
  shopName: string | null;
  activityDescription: string | null;
  logoFileUrl: string | null;
  pendingShopName: string | null;
  pendingActivityDescription: string | null;
  pendingLogoFileUrl: string | null;
  profileChangesSubmittedAt: string | null;
  seller: { id: number; slug: string; shopName: string } | null;
}

export const sellerApplicationsModerationApi = {
  list: (status?: SellerApplicationStatus, search?: string) => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    const q = search?.trim();
    if (q) params.set('search', q);
    const query = params.toString();
    return apiClient.get<SellerApplicationRow[]>(
      query
        ? `/seller-applications/moderation?${query}`
        : '/seller-applications/moderation',
    );
  },

  getOne: (id: number) =>
    apiClient.get<SellerApplicationRow & Record<string, unknown>>(
      `/seller-applications/moderation/${id}`,
    ),

  /** Временная ссылка на паспорт / ЕГРЮЛ / оферту заявки — файлы в закрытом бакете. */
  documentLink: (id: number, url: string) =>
    apiClient.post<{ url: string }>(`/seller-applications/moderation/${id}/document-link`, { url }),

  approve: (id: number) =>
    apiClient.post<SellerApplicationRow & Record<string, unknown>>(
      `/seller-applications/moderation/${id}/approve`,
      {},
    ),

  enrollTbankSe: (id: number) =>
    apiClient.post<SellerApplicationRow & Record<string, unknown>>(
      `/seller-applications/moderation/${id}/enroll-tbank-se`,
      {},
    ),

  reject: (
    id: number,
    body: { rejectionKind: SellerApplicationRejectionKind; comment?: string },
  ) =>
    apiClient.post<SellerApplicationRow>(
      `/seller-applications/moderation/${id}/reject`,
      body,
    ),

  listProfileChanges: () =>
    apiClient.get<SellerProfileChangesRow[]>('/seller-applications/moderation/profile-changes'),

  /** Без `fields` — одобрить все изменения витрины. */
  approveProfileChanges: (id: number, fields?: SellerProfileChangeField[]) =>
    apiClient.post<SellerApplicationRow & Record<string, unknown>>(
      `/seller-applications/moderation/${id}/profile-changes/approve`,
      fields ? { fields } : {},
    ),

  /** Без `fields` — отклонить все изменения витрины. */
  rejectProfileChanges: (id: number, body: { reason: string; fields?: SellerProfileChangeField[] }) =>
    apiClient.post<SellerApplicationRow & Record<string, unknown>>(
      `/seller-applications/moderation/${id}/profile-changes/reject`,
      body,
    ),
};
